import React, { useState, useEffect, useCallback } from 'react';
import { Headache, Tension, Lloro } from '../../types';
import './Calendar.css';

// Función helper para iconos de ubicación
const getLocationIcon = (location: string) => {
  switch (location) {
    case 'Frente':
      return '👁️';
    case 'Sien':
      return '⚡';
    case 'Parte Superior':
      return '⬆️';
    case 'Nuca':
      return '🔙';
    case 'Toda la Cabeza':
      return '🤕';
    default:
      return '📍';
  }
};

// Función para formatear ubicación con iconos
const formatLocationWithIcons = (location: string) => {
  if (!location) return '';
  
  // Dividir por coma con o sin espacio para manejar ambos formatos
  const locations = location.split(/,\s*/).map(loc => {
    const icon = getLocationIcon(loc.trim());
    return `${icon} ${loc.trim()}`;
  });
  
  return locations.join(', ');
};

// Helpers para colores de intensidad
const getIntensityColor = (intensity: string): string => {
  switch (intensity) {
    case 'leve':
      return '#22c55e';
    case 'moderada':
      return '#f59e0b';
    case 'fuerte':
      return '#ef4444';
    default:
      return '#22c55e';
  }
};

// Helper para emojis de intensidad
const getIntensityEmoji = (intensity: string): string => {
  switch (intensity) {
    case 'leve':
      return '😐';
    case 'moderada':
      return '😣';
    case 'fuerte':
      return '😰';
    default:
      return '😐';
  }
};

const getIntensityClass = (intensity: string): string => {
  switch (intensity) {
    case 'leve':
      return 'intensity-mild';
    case 'moderada':
      return 'intensity-moderate';
    case 'fuerte':
      return 'intensity-severe';
    default:
      return 'intensity-mild';
  }
};

interface CalendarProps {
  mode: 'headaches' | 'tensions' | 'lloros';
  headaches: Headache[];
  tensions: Tension[];
  lloros?: Lloro[];
  onDateClick: (date: string, existingItem?: Headache | Tension | Tension[] | Lloro | Lloro[]) => void;
  onCurrentDateChange?: (date: Date) => void;
}

const Calendar: React.FC<CalendarProps> = ({ mode, headaches, tensions, lloros = [], onDateClick, onCurrentDateChange }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  interface CalendarDay {
    date: Date;
    headache?: Headache;
    tension?: Tension;
    tensions?: Tension[]; // Para manejar múltiples tensiones
    lloro?: Lloro;
    lloros?: Lloro[]; // Para manejar múltiples lloros
  }

  const generateCalendarDays = useCallback((): CalendarDay[] => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // Primer y último día del mes
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Días a mostrar antes del primer día del mes
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    // Días a mostrar después del último día del mes
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));
    
    const days: CalendarDay[] = [];
    const currentDay = new Date(startDate);
    
    while (currentDay <= endDate) {
      const dateStr = currentDay.toISOString().split('T')[0];
      
      const headache = headaches.find(h => {
        const headacheDate = new Date(h.date);
        return headacheDate.toDateString() === currentDay.toDateString();
      });
      
      const tension = tensions.find(t => {
        const tensionDate = new Date(t.date);
        return tensionDate.toDateString() === currentDay.toDateString();
      });

      // Obtener todas las tensiones del día (no solo la primera)
      const dayTensions = tensions.filter(t => {
        const tensionDate = new Date(t.date);
        return tensionDate.toDateString() === currentDay.toDateString();
      });

      // Obtener el primer lloro del día
      const lloro = lloros.find(l => {
        const lloroDate = new Date(l.date);
        return lloroDate.toDateString() === currentDay.toDateString();
      });

      // Obtener todos los lloros del día
      const dayLloros = lloros.filter(l => {
        const lloroDate = new Date(l.date);
        return lloroDate.toDateString() === currentDay.toDateString();
      });

      days.push({
        date: new Date(currentDay),
        headache,
        tension, // Mantener para compatibilidad
        tensions: dayTensions, // Array de todas las tensiones del día
        lloro,
        lloros: dayLloros // Array de todos los lloros del día
      });

      currentDay.setDate(currentDay.getDate() + 1);
    }

    return days;
  }, [currentDate, headaches, tensions, lloros, mode]);

  useEffect(() => {
    if (onCurrentDateChange) {
      onCurrentDateChange(currentDate);
    }
  }, [currentDate, onCurrentDateChange]);

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date: Date): boolean => {
    return date.getMonth() === currentDate.getMonth();
  };

  const handleDateClick = (day: CalendarDay) => {
    const dateStr = day.date.toISOString().split('T')[0];
    if (mode === 'headaches') {
      onDateClick(dateStr, day.headache);
    } else if (mode === 'tensions') {
      // Para tensiones, pasar todas las tensiones del día
      onDateClick(dateStr, day.tensions && day.tensions.length > 0 ? day.tensions : undefined);
    } else if (mode === 'lloros') {
      // Para lloros, pasar todos los lloros del día
      onDateClick(dateStr, day.lloros && day.lloros.length > 0 ? day.lloros : undefined);
    }
  };

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  return (
    <div className="calendar-container">
      {/* Header del calendario */}
      <div className="calendar-header">
        <button 
          onClick={() => navigateMonth('prev')}
          className="nav-btn"
          aria-label="Mes anterior"
        >
          ‹
        </button>
        
        <h3 className="month-title">{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h3>
        
        <div className="header-right">
          <button onClick={goToToday} className="today-btn">
            Hoy
          </button>
          
          <button 
            onClick={() => navigateMonth('next')}
            className="nav-btn"
            aria-label="Mes siguiente"
          >
            ›
          </button>
        </div>
      </div>

      {/* Días de la semana */}
      <div className="weekdays">
        {dayNames.map(day => (
          <div key={day} className="weekday">
            {day}
          </div>
        ))}
      </div>

      {/* Días del calendario */}
      <div className="calendar-grid">
        <div className="days-grid">
        {generateCalendarDays().map((day, index) => {
          const hasData = mode === 'headaches'
            ? day.headache
            : mode === 'tensions'
            ? (day.tensions && day.tensions.length > 0)
            : (day.lloros && day.lloros.length > 0);

          let title = '';
          if (hasData) {
            if (mode === 'headaches') {
              title = day.headache ? `Dolor de cabeza intensidad ${day.headache.intensity}` : 'Agregar dolor de cabeza';
            } else if (mode === 'tensions') {
              const tensionCount = day.tensions?.length || 0;
              if (tensionCount > 1) {
                title = `${tensionCount} registros de tensión`;
              } else if (tensionCount === 1) {
                title = `Tensión ${day.tensions![0].systolic}/${day.tensions![0].diastolic}`;
              } else {
                title = 'Agregar tensión';
              }
            } else if (mode === 'lloros') {
              const lloroCount = day.lloros?.length || 0;
              if (lloroCount > 1) {
                title = `${lloroCount} registros de lloro`;
              } else if (lloroCount === 1) {
                title = `Lloro intensidad ${day.lloros![0].intensity}`;
              } else {
                title = 'Agregar lloro';
              }
            }
          } else {
            title = mode === 'headaches' ? 'Agregar dolor de cabeza' : mode === 'tensions' ? 'Agregar tensión' : 'Agregar lloro';
          }

          // Get lloro intensity color
          const getLloroIntensityColor = (intensity: string): string => {
            switch (intensity) {
              case 'leve': return '#10b981';
              case 'moderado': return '#f59e0b';
              case 'fuerte': return '#ef4444';
              default: return '#60a5fa';
            }
          };

          return (
            <div
              key={index}
              className={`calendar-day ${
                isToday(day.date) ? 'today' : ''
              } ${
                !isCurrentMonth(day.date) ? 'other-month' : ''
              } ${
                hasData ? (mode === 'headaches' ? 'has-headache' : mode === 'tensions' ? 'has-tension' : 'has-lloro') : ''
              } ${
                mode === 'headaches' && day.headache ? getIntensityClass(day.headache.intensity) : ''
              }`}
              onClick={() => handleDateClick(day)}
              title={title}
              style={mode === 'headaches' && day.headache ? {
                borderColor: getIntensityColor(day.headache.intensity),
                backgroundColor: `${getIntensityColor(day.headache.intensity)}20`
              } : mode === 'tensions' && day.tensions && day.tensions.length > 0 ? {
                borderColor: '#3b82f6',
                backgroundColor: '#3b82f620'
              } : mode === 'lloros' && day.lloros && day.lloros.length > 0 ? {
                borderColor: getLloroIntensityColor(day.lloros[0].intensity),
                backgroundColor: `${getLloroIntensityColor(day.lloros[0].intensity)}20`
              } : {}}
            >
              <span className="day-number">
                {day.date.getDate()}
              </span>

              {/* Indicador de datos existentes */}
              {hasData && (
                <div className="indicators">
                  {mode === 'headaches' && day.headache && (
                    <div className="headache-indicator">
                      <div className="intensity-dot" style={{ backgroundColor: getIntensityColor(day.headache.intensity) }}></div>
                      <div className="location-emojis">{getLocationIcon(day.headache.location || '')}</div>
                    </div>
                  )}
                  
                  {mode === 'tensions' && day.tensions && day.tensions.length > 0 && (
                    <div className="tension-indicator">
                      <div className="tension-dot" style={{ backgroundColor: '#3b82f6' }}></div>
                      {day.tensions.length === 1 ? (
                        <div className="tension-values">{day.tensions[0].systolic}/{day.tensions[0].diastolic}</div>
                      ) : (
                        <div className="tension-values">{day.tensions.length}x</div>
                      )}
                    </div>
                  )}

                  {mode === 'lloros' && day.lloros && day.lloros.length > 0 && (
                    <div className="lloro-indicator">
                      <div className="lloro-dot" style={{ backgroundColor: getLloroIntensityColor(day.lloros[0].intensity) }}></div>
                      {day.lloros.length === 1 ? (
                        <div className="lloro-emoji">😢</div>
                      ) : (
                        <div className="lloro-count">{day.lloros.length}x</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        </div>
      </div>

      {/* Leyenda */}
      <div className="calendar-legend">
        {mode === 'headaches' && (
          <>
            <div className="legend-item">
              <div className="legend-dot intensity-mild"></div>
              <span>Leve</span>
            </div>
            <div className="legend-item">
              <div className="legend-dot intensity-moderate"></div>
              <span>Moderada</span>
            </div>
            <div className="legend-item">
              <div className="legend-dot intensity-severe"></div>
              <span>Fuerte</span>
            </div>
          </>
        )}
        {mode === 'tensions' && (
          <>
            <div className="legend-item">
              <div className="legend-dot" style={{ backgroundColor: '#3b82f6' }}></div>
              <span>Tensión registrada</span>
            </div>
          </>
        )}
        {mode === 'lloros' && (
          <>
            <div className="legend-item">
              <div className="legend-dot" style={{ backgroundColor: '#10b981' }}></div>
              <span>Leve</span>
            </div>
            <div className="legend-item">
              <div className="legend-dot" style={{ backgroundColor: '#f59e0b' }}></div>
              <span>Moderado</span>
            </div>
            <div className="legend-item">
              <div className="legend-dot" style={{ backgroundColor: '#ef4444' }}></div>
              <span>Fuerte</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Calendar;